/**
 * Notifications & Watchers Hooks
 *
 * Follow/Watch work orders and receive notifications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import type {
  Notification,
  WorkOrderWatcher,
  UserNotificationPreferences,
  WatchedWorkOrder,
  UnreadNotificationCount,
} from '@/types'

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unread: () => [...notificationKeys.all, 'unread'] as const,
  unreadCount: () => [...notificationKeys.all, 'count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
  watchers: () => ['watchers'] as const,
  watchersByWO: (woId: string) => [...notificationKeys.watchers(), 'wo', woId] as const,
  watchedByUser: () => [...notificationKeys.watchers(), 'user'] as const,
  isWatching: (woId: string) => [...notificationKeys.watchers(), 'watching', woId] as const,
}

// =====================================================
// NOTIFICATIONS
// =====================================================

export function useNotifications(options?: { limit?: number; unreadOnly?: boolean }) {
  const { limit = 50, unreadOnly = false } = options ?? {}

  return useQuery({
    queryKey: unreadOnly ? notificationKeys.unread() : notificationKeys.list(),
    queryFn: async () => {
      let query = supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (unreadOnly) {
        query = query.eq('read', false)
      }

      const { data, error } = await query
      if (error) throw error
      return data as Notification[]
    },
  })
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_unread_notification_count')
        .select('*')
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return (data as UnreadNotificationCount) ?? { user_id: '', unread_count: 0, urgent_count: 0 }
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('read', false)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

// =====================================================
// NOTIFICATION PREFERENCES
// =====================================================

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return data as UserNotificationPreferences | null
    },
  })
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (prefs: Partial<Omit<UserNotificationPreferences, 'id' | 'user_id' | 'created_at'>>) => {
      // Upsert preferences
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: user.id,
          ...prefs,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' })
        .select()
        .single()

      if (error) throw error
      return data as UserNotificationPreferences
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() })
    },
  })
}

// =====================================================
// WORK ORDER WATCHERS
// =====================================================

export function useWatchersForWorkOrder(workOrderId: string | null) {
  return useQuery({
    queryKey: notificationKeys.watchersByWO(workOrderId ?? 'none'),
    queryFn: async () => {
      if (!workOrderId) return []

      const { data, error } = await supabase
        .from('work_order_watchers')
        .select(`
          *,
          users:user_id (
            id,
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('work_order_id', workOrderId)

      if (error) throw error
      return data as (WorkOrderWatcher & {
        users: { id: string; full_name: string; email: string; avatar_url: string | null }
      })[]
    },
    enabled: !!workOrderId,
  })
}

export function useWatchedWorkOrders() {
  return useQuery({
    queryKey: notificationKeys.watchedByUser(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_my_watched_work_orders')
        .select('*')
        .order('last_update', { ascending: false })

      if (error) throw error
      return data as WatchedWorkOrder[]
    },
  })
}

export function useIsWatchingWorkOrder(workOrderId: string | null) {
  return useQuery({
    queryKey: notificationKeys.isWatching(workOrderId ?? 'none'),
    queryFn: async () => {
      if (!workOrderId) return false

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      const { data, error } = await supabase
        .from('work_order_watchers')
        .select('id')
        .eq('work_order_id', workOrderId)
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return !!data
    },
    enabled: !!workOrderId,
  })
}

export function useWatchWorkOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      workOrderId,
      organizationId,
      preferences,
    }: {
      workOrderId: string
      organizationId: string
      preferences?: Partial<Pick<WorkOrderWatcher,
        'notify_on_status_change' | 'notify_on_comment' | 'notify_on_assignment' | 'notify_on_completion' | 'notify_on_invoice'
      >>
    }) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('work_order_watchers')
        .insert({
          work_order_id: workOrderId,
          user_id: user.id,
          organization_id: organizationId,
          watch_reason: 'manual',
          ...preferences,
        })
        .select()
        .single()

      if (error) throw error
      return data as WorkOrderWatcher
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.watchersByWO(data.work_order_id) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.isWatching(data.work_order_id) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.watchedByUser() })
    },
  })
}

export function useUnwatchWorkOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (workOrderId: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('work_order_watchers')
        .delete()
        .eq('work_order_id', workOrderId)
        .eq('user_id', user.id)

      if (error) throw error
      return workOrderId
    },
    onSuccess: (workOrderId) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.watchersByWO(workOrderId) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.isWatching(workOrderId) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.watchedByUser() })
    },
  })
}

export function useUpdateWatchPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      workOrderId,
      preferences,
    }: {
      workOrderId: string
      preferences: Partial<Pick<WorkOrderWatcher,
        'notify_on_status_change' | 'notify_on_comment' | 'notify_on_assignment' |
        'notify_on_completion' | 'notify_on_invoice' | 'notify_email' | 'notify_in_app' | 'notify_push'
      >>
    }) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('work_order_watchers')
        .update(preferences)
        .eq('work_order_id', workOrderId)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) throw error
      return data as WorkOrderWatcher
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.watchersByWO(data.work_order_id) })
    },
  })
}
